'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { c } from '../../../utils/tailwind-merge';
import { IconButton } from '../../atoms/Button/IconButton';
import { InputV2 } from '../../atoms/Input.v2';
import { AccountTypeToggleGroup } from '../../molecules/AccountTypeToggleGroup/AccountTypeToggleGroup';
import { FormModal } from '../../molecules/FormModal/FormModal';
import { VisibilityToggleGroup } from '../../molecules/VisibilityToggleGroup/VisibilityToggleGroup';
import {
  LIST_NAME_MAX_LENGTH,
  favoriteListModalFormSchema,
} from './FavoriteListModal.schema';
import type {
  FavoriteListModalFormData,
  FavoriteListModalProps,
} from './FavoriteListModal.types';

type FormLabelProps = {
  htmlFor: string;
  children: React.ReactNode;
  className?: string;
};

const FormLabel = ({ htmlFor, children, className = '' }: FormLabelProps) => (
  <label
    htmlFor={htmlFor}
    className={c(
      'text-sm font-normal leading-6 tracking-tight text-grey-100',
      className
    )}
  >
    {children}
  </label>
);

export const FavoriteListModal = ({
  listName,
  visibility,
  accountType,
  showAccountType,
  userName,
  userProfilePicture,
  publisherName,
  microcopy,
  onSave,
  trigger,
  className = '',
}: FavoriteListModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    watch,
  } = useForm<FavoriteListModalFormData>({
    resolver: zodResolver(favoriteListModalFormSchema),
    mode: 'onChange',
    defaultValues: {
      listName,
      visibility,
      accountType: showAccountType ? accountType : undefined,
    },
  });

  const watchedListName = watch('listName');

  const onFormSubmit = (data: FavoriteListModalFormData) => {
    onSave({
      listName: data.listName,
      visibility: data.visibility,
      ...(showAccountType &&
        data.accountType && { accountType: data.accountType }),
    });
  };

  const triggerElement = trigger ?? (
    <IconButton
      className="h-12 w-12 overflow-hidden rounded-lg bg-grey-700 hover:bg-grey-800"
      iconName="BookmarkSimple"
      ariaLabel={microcopy.cta}
      iconWeight="bold"
      iconSize={24}
      iconClassName="fill-white hover:fill-white"
    />
  );

  const formContent = (
    <form
      id="favorite-list-form"
      onSubmit={handleSubmit(onFormSubmit)}
      className="flex flex-col gap-3 py-4"
    >
      <div className="flex flex-col gap-2">
        <FormLabel htmlFor="list-name-input">
          {microcopy.listNameLabel}
        </FormLabel>
        <div className="relative">
          <Controller
            control={control}
            name="listName"
            render={({ field }) => (
              <InputV2
                id="list-name-input"
                dark
                {...field}
                placeholder={microcopy.listNamePlaceholder}
                className="w-full pr-10"
                maxLength={LIST_NAME_MAX_LENGTH}
                showRemainingChars
              />
            )}
          />
          {watchedListName && (
            <IconButton
              iconName="XCircle"
              ariaLabel={microcopy.clearInput}
              className="absolute right-2.5 top-[10px] h-5 w-5 bg-transparent"
              iconClassName="fill-grey-500 hover:fill-grey-500"
              iconWeight="fill"
              onClick={() => setValue('listName', '', { shouldValidate: true })}
              iconSize={20}
            />
          )}
        </div>
      </div>

      {showAccountType && (
        <div className="flex flex-col gap-2">
          <FormLabel htmlFor="account-type-toggle">
            {microcopy.accountTypeLabel}
          </FormLabel>
          <Controller
            control={control}
            name="accountType"
            render={({ field }) => (
              <AccountTypeToggleGroup
                userName={userName}
                userProfilePicture={userProfilePicture}
                publisherName={publisherName}
                value={field.value || 'personal'}
                microcopy={microcopy.accountType}
                onValueChange={field.onChange}
              />
            )}
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <FormLabel htmlFor="visibility-toggle">
          {microcopy.visibilityLabel}
        </FormLabel>
        <Controller
          control={control}
          name="visibility"
          render={({ field }) => (
            <VisibilityToggleGroup
              value={field.value}
              microcopy={microcopy.visibility}
              onValueChange={field.onChange}
            />
          )}
        />
      </div>
    </form>
  );

  return (
    <FormModal
      dark
      size="xsmall"
      trigger={triggerElement}
      title={microcopy.title}
      subtitle={microcopy.subTitle}
      close={microcopy.close}
      submit={microcopy.cta}
      disabled={!isValid}
      onSubmit={handleSubmit(onFormSubmit)}
      content={formContent}
      className={c('h-auto max-h-[468px]', className)}
    />
  );
};

FavoriteListModal.displayName = 'FavoriteListModal';
